/**
 * Extracts linked list row ids for a LIST table block from GET passport `stats`.
 *
 * When `stats.multi` is present (several перечни on one object), each row is scoped by
 * `parent_id` === `field_groups` / table `list_group_id`. We must **not** fall back to
 * top-level `object_ids` in that case: that array is typically merged across all list types
 * (e.g. «Линии» + «Светильники»), which would show wrong rows in a single table.
 */
export function extractLinkedObjectIdsForListGroup(
  stats: unknown,
  listGroupId: number | undefined | null,
): number[] {
  if (listGroupId == null || Number(listGroupId) <= 0) return []
  const lid = Number(listGroupId)
  const s = stats as Record<string, unknown> | null
  if (!s || typeof s !== 'object') return []

  const multi = s.multi
  if (Array.isArray(multi) && multi.length > 0) {
    for (const row of multi) {
      const r = row as { parent_id?: number; object_ids?: unknown[] }
      if (r.parent_id === lid && Array.isArray(r.object_ids)) {
        return r.object_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
      }
    }
    return []
  }

  const single = s.single as { object_ids?: unknown[] } | undefined
  if (single && Array.isArray(single.object_ids) && single.object_ids.length > 0) {
    return single.object_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
  }

  const ids = s.object_ids
  if (Array.isArray(ids)) {
    return ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
  }

  return []
}

/**
 * Extracts linked list row ids from GET passport `stats.by_type` subtypes.
 *
 * Iterates over `stats.by_type`, then over each type's `subtypes`,
 * and collects `ids` from subtypes whose `invent_type_id` matches
 * the expected `listInventTypeId` (resolved from groups via `list_group_id`).
 */
export function extractLinkedObjectIdsFromByType(
  stats: unknown,
  listInventTypeId: number,
): number[] {
  if (!Number.isFinite(listInventTypeId) || listInventTypeId <= 0) return []
  const s = stats as Record<string, unknown> | null
  if (!s || typeof s !== 'object') return []

  const byType = s.by_type
  if (!byType || typeof byType !== 'object') return []

  const ids: number[] = []
  for (const typeEntry of Object.values(byType as Record<string, unknown>)) {
    if (!typeEntry || typeof typeEntry !== 'object') continue
    const subtypes = (typeEntry as Record<string, unknown>).subtypes
    if (!subtypes || typeof subtypes !== 'object') continue
    for (const subtypeEntry of Object.values(subtypes as Record<string, unknown>)) {
      if (!subtypeEntry || typeof subtypeEntry !== 'object') continue
      if (Number((subtypeEntry as Record<string, unknown>).invent_type_id) === listInventTypeId) {
        const objectIds = (subtypeEntry as Record<string, unknown>).ids
        if (Array.isArray(objectIds)) {
          ids.push(...objectIds.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0))
        }
      }
    }
  }
  return ids
}
